import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, InputBase, Link, Paper, Stack, Typography } from '@mui/material';
import { ArrowForward as ArrowForwardIcon, Forum as ForumIcon } from '@mui/icons-material';
import { useSystemConfig } from '../../hooks/useSystemConfig';

const IDEAS = [
  'Read receipt photos and return the store, date and total',
  'A support chatbot that answers from our help docs',
  'Research a topic, summarize the sources and email me a report',
];

interface BuildHeroProps {
  hasActiveSubscription: boolean;
  onShowUseCases: () => void;
}

/** Dashboard entry point: describe an idea and continue in the Build Guide. */
export const BuildHero: React.FC<BuildHeroProps> = ({ hasActiveSubscription, onShowUseCases }) => {
  const navigate = useNavigate();
  const guideOn = useSystemConfig().features.build_guide;
  const [idea, setIdea] = useState('');
  const text = idea.trim();

  const openFunctionBuilder = () =>
    navigate(text ? `/dashboard/ai-functions?prompt=${encodeURIComponent(text)}` : '/dashboard/ai-functions');

  const start = () => {
    if (!hasActiveSubscription) {
      navigate('/dashboard/subscription');
      return;
    }
    if (!guideOn) {
      openFunctionBuilder();
      return;
    }
    navigate(text ? `/dashboard/guide?start=${encodeURIComponent(text)}` : '/dashboard/guide');
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: { xs: 2.5, md: 3.5 },
        borderRadius: 3,
        color: 'common.white',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #4f46e5 100%)',
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
        <ForumIcon />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          What do you want to build?
        </Typography>
        {guideOn && (
          <Chip
            label="Build Guide · best place to start"
            size="small"
            sx={{ color: 'common.white', bgcolor: 'rgba(255,255,255,0.18)', fontWeight: 600 }}
          />
        )}
      </Stack>
      <Typography sx={{ opacity: 0.85, mb: 2.5, maxWidth: 760 }}>
        {guideOn
          ? 'Describe your idea in plain words, no technical knowledge needed. The Build Guide asks a couple of questions, recommends the right setup (a function, a chain, a chatbot or an app) and opens the right builder with everything filled in.'
          : 'Describe it in plain words and the Function Builder will draft it for you.'}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          p: 1,
          pl: 2,
          borderRadius: 2,
          bgcolor: 'common.white',
          boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
        }}
      >
        <InputBase
          fullWidth
          multiline
          maxRows={4}
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              start();
            }
          }}
          placeholder="E.g. When a customer emails a photo of their meal, estimate the calories and reply with a summary"
          inputProps={{ 'aria-label': 'Describe what you want to build', maxLength: 4000 }}
          sx={{ py: 0.75, fontSize: '1rem' }}
        />
        <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={start} sx={{ flexShrink: 0 }}>
          {hasActiveSubscription ? 'Start' : 'See plans'}
        </Button>
      </Box>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
        {IDEAS.map((example) => (
          <Chip
            key={example}
            label={example}
            size="small"
            onClick={() => setIdea(example)}
            sx={{ color: 'common.white', bgcolor: 'rgba(255,255,255,0.14)', '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' } }}
          />
        ))}
      </Stack>

      <Stack direction="row" spacing={2.5} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
        {!hasActiveSubscription && (
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            Building needs an active plan.
          </Typography>
        )}
        {hasActiveSubscription && guideOn && (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={openFunctionBuilder}
            sx={{ color: 'common.white', opacity: 0.9, fontSize: '0.875rem' }}
          >
            Know exactly what you want? Go straight to the Function Builder
          </Link>
        )}
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={onShowUseCases}
          sx={{ color: 'common.white', opacity: 0.9, fontSize: '0.875rem' }}
        >
          What can I do with EasyAI?
        </Link>
      </Stack>
    </Paper>
  );
};

export default BuildHero;
